import React from "react"
import { useTranslation } from "react-i18next"
import AWS from "aws-sdk/global"
import { SNS } from "aws-sdk"

import { useAuth, useAlert, useTheme } from "hooks"

import FormElement from "components/Form/FormElement"
import { Markdown } from "components/Elements/Markdown"
import {
	userPermissionList,
	BIKE_ID_MAX_LENGTH,
	UPDATE_BIKE_INFO_SUBJECT,
	BC_OPERATOR,
	MC_OPERATOR,
} from "utils/constants"
import { sanitizeCode, isBikeCodeValid } from "utils"

import {
	bcSubjectArray,
	mfcSubjectArray,
	ACTIVATION_LINK_SUBJECT,
	BIKE_DESCRIPTION_SUBJECT,
	CONSUMER_QUESTION_SUBJECT,
	ARCADE_PARTNER_SUBJECT,
	BUYING_TAG_SUBJECT,
	UPDATE_PARTNER_SUBJECT,
	BACK_STORE_BIKES_SUBJECT,
	MFC_OPERATOR,
	VELOCISTE_GENDER,
} from "utils/constants"
import { FormError } from "models/form"

const defaultFormFields = {
	subject: "",
	fnuci: "",
	message: "",
}

const defaultFormEvents = {
	ready: true,
}

export const ContactForm = () => {
	const [formFields, setFormFields] = React.useState(defaultFormFields)
	const [formEvents, setFormEvents] = React.useState(defaultFormEvents)
	const [errors, setErrors] = React.useState([])
	const [showLocalErrors, setShowLocalErrors] = React.useState(false)
	const [isMessageSent, setIsMessageSent] = React.useState(false)

	const { t } = useTranslation()
	const { alert } = useAlert()
	const { entity, user } = useAuth()
	const { currentTheme } = useTheme()

	const isUserSubject =
		formFields.subject === ACTIVATION_LINK_SUBJECT ||
		formFields.subject === CONSUMER_QUESTION_SUBJECT ||
		formFields.subject === ARCADE_PARTNER_SUBJECT ||
		formFields.subject === UPDATE_PARTNER_SUBJECT ||
		formFields.subject === BUYING_TAG_SUBJECT

	const isRegisterErrorSubject =
		formFields.subject === BIKE_DESCRIPTION_SUBJECT ||
		formFields.subject === UPDATE_BIKE_INFO_SUBJECT ||
		formFields.subject === BACK_STORE_BIKES_SUBJECT

	const isMFCCurrentTheme = currentTheme.id_api === MFC_OPERATOR

	const userPermission = userPermissionList.find((p) => p.value === user?.permission)?.label
	// Initialize the Amazon Cognito credentials provider
	AWS.config.region = process.env.REACT_APP_AWS_CONFIG_REGION // Region
	AWS.config.credentials = new AWS.CognitoIdentityCredentials({
		IdentityPoolId: process.env.REACT_APP_AWS_IDENTITY_POOL_ID,
	})

	// In production mode, we need to send the form to a specific mailbox
	// Switch between only 3 operators : MFC / Bicycode / Moustache
	const topicArn = () => {
		switch (currentTheme.id_api) {
			case MFC_OPERATOR:
				return process.env.REACT_APP_AWS_TOPIC_ARN_CONTACT_FORM_MFC
			case BC_OPERATOR:
				return entity.gender === VELOCISTE_GENDER
					? process.env.REACT_APP_AWS_TOPIC_ARN_CONTACT_FORM_PRO
					: process.env.REACT_APP_AWS_TOPIC_ARN_CONTACT_FORM_AS
			case MC_OPERATOR:
				return process.env.REACT_APP_AWS_TOPIC_ARN_CONTACT_FORM_MC
		}
	}

	const handleInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const target = event.target
		const value = target.type === "checkbox" ? target.checked : target.value
		const name = target.name
		setFormFields((s) => ({
			...s,
			[name]: value,
		}))
	}, [])

	const handleValidation = React.useCallback((name: string, errs: Array<FormError>) => {
		setErrors((s) => {
			const cleanErrors = [...s.filter((e) => e.origin !== name)]
			return [...cleanErrors, ...errs]
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleSubmit = async (event: React.SyntheticEvent) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			// hide error
			setShowLocalErrors(false)
			// prevent form to be submitted again
			setFormEvents({
				...formEvents,
				ready: false,
			})

			const { message, subject, fnuci } = formFields

			const sns = new SNS()
			const content = `
				N° déclarateur: ${entity.entity_id}\n
				Entreprise: ${entity.entity_data.raison_sociale}\n
				Mail: ${user.mail}\n
				Role du demandeur: ${userPermission}\n
				${fnuci && `Code FNUCI: ${sanitizeCode(fnuci)}\n`}
				Sujet: ${subject}\n
				Message: ${message}\n
			`
			const params = {
				Message: content,
				Subject: `${subject} ${fnuci && "-"} ${sanitizeCode(fnuci)}`,
				TopicArn:
					process.env.REACT_APP_MODE === "production"
						? topicArn()
						: process.env.REACT_APP_AWS_TOPIC_ARN_CONTACT_FORM,
			}
			sns.publish(params, function (err, data) {
				if (err) {
					//console.log(err, err.stack)
					alert("error", err.stack) // an error occurred
					setFormEvents({
						...formEvents,
						ready: true,
					})
				} else {
					//console.log(data)
					setIsMessageSent(true) // successful response
				}
			})
		}
	}

	const subjectCollectionForBC = bcSubjectArray.map((subject) => ({
		label: t(`${subject}`),
		value: t(`${subject}`),
	}))

	const subjectCollectionForMFC = mfcSubjectArray.map((subject) => ({
		label: t(`${subject}`),
		value: t(`${subject}`),
	}))

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleInputChange,
	}

	return (
		<form className="c-card p-5 md:p-12 mb-12">
			<h2 className="c-h2 font-semibold mb-7 uppercase">
				{t("contact_form_title", { ns: "contact" })}
			</h2>

			{!isMessageSent && (
				<>
					<div className="mb-5">
						<Markdown>{t("contact_description", { ns: "contact" })}</Markdown>
					</div>

					<div className="lg:w-1/2 w-full py-4">
						<FormElement
							{...formElementCommonProps}
							value={formFields.subject}
							options={
								isMFCCurrentTheme ? subjectCollectionForMFC : subjectCollectionForBC
							}
							placeholder={t("choose_subject", { ns: "contact" })}
							name="subject"
							type="select"
							label={t("subject", { ns: "contact" })}
							className="mb-5"
							required={true}
						/>
					</div>

					{formFields.subject && (
						<>
							{!isUserSubject && (
								<div className="lg:w-1/2 w-full py-4">
									<FormElement
										{...formElementCommonProps}
										value={sanitizeCode(formFields.fnuci)}
										name="fnuci"
										type="text"
										label={t("history.activity_headers.code", {
											ns: "partner",
										})}
										className="mb-5"
										required={isRegisterErrorSubject}
										maxLength={BIKE_ID_MAX_LENGTH}
										customRules={[
											{
												key: "bikeCode-empty",
												check: (value: string) =>
													isRegisterErrorSubject
														? isBikeCodeValid(value)
														: true,
												message: t("code_min_length", { ns: "form" }),
											},
										]}
									/>
								</div>
							)}

							<div className="l-col-12 py-4">
								<FormElement
									{...formElementCommonProps}
									value={formFields.message}
									name="message"
									type="textarea"
									label={t("message", { ns: "contact" })}
									className="mb-5"
									required={true}
								/>
							</div>

							{formEvents.ready && (
								<div className="flex justify-end">
									<button
										type="submit"
										className="c-btn c-btn--primary c-btn--m px-12"
										onClick={handleSubmit}
									>
										{t("global.send")}
									</button>
								</div>
							)}

							{!formEvents.ready && (
								<div className="c-spinner mt-5 c-spinner--light"></div>
							)}
						</>
					)}
				</>
			)}
			{isMessageSent && (
				<div className="text-center py-4">
					<p className="font-bold">{t("email_send", { ns: "contact" })}</p>
				</div>
			)}
		</form>
	)
}
